import { MagnifyingGlassIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useStore } from "@nanostores/react";
import { ReactElement, useCallback, useEffect, useRef, useState } from "react";

import { Entity } from "../../api";
import { colorSchemas } from "../../config/entities.ts";
import { useEntityCache } from "../../hooks/useEntityCache.ts";
import { useFloatingHover } from "../../hooks/useFloatingHover.ts";
import { getViewportCenter } from "../../services/graph/get-viewport-center.ts";
import { createGraphNodeModel } from "../../services/graph/user-actions/create-graph-node-model.ts";
import { nodeOperations } from "../../services/graph/user-actions/node-operations.ts";
import { $isEntityCreationFormOpen } from "../../stores/graph/entities.ts";
import { $cytoscapeInstance } from "../../stores/graph/ui.ts";
import { $graphDragEvent } from "../../stores/graph/ui.ts";
import { $router } from "../../stores/router.ts";
import { $userAppSettings } from "../../stores/user-app-settings.ts";
import { isStickyNoteEntity } from "../../utils/sticky-note-utils.ts";
import { Button } from "../catalyst/Button.tsx";
import { Input } from "../catalyst/Input.tsx";
import { InputGroup } from "../catalyst/InputGroup.tsx";
import { DraggableMenuItem } from "../DraggableMenuItem.tsx";
import { createIconComponent } from "../icons/getRawSvgForIcon.tsx";

export const FloatingEntityList = (): ReactElement => {
  const router = useStore($router);
  const cytoscapeInstance = useStore($cytoscapeInstance);
  const userAppSettings = useStore($userAppSettings);
  const graphId = router?.route === "graph" ? router.params.id : null;
  const [searchTerm, setSearchTerm] = useState("");
  const entityListRef = useRef<HTMLDivElement>(null);
  const dragCompleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Entity cache for the entity list
  const { entities, isLoading, loadOptions, searchResults, selectEntity } =
    useEntityCache();

  const {
    handleMenuMouseEnter,
    handleMenuMouseLeave,
    handleMouseEnter,
    handleMouseLeave,
    isOpen: isEntityListOpen,
  } = useFloatingHover({
    onClose: () => {
      if (dragCompleteTimeoutRef.current) {
        window.clearTimeout(dragCompleteTimeoutRef.current);
        dragCompleteTimeoutRef.current = null;
      }
    },
  });

  // Listen to global drag events to detect successful drops
  useEffect(() => {
    const unsubscribe = $graphDragEvent.subscribe((dragEvent) => {
      if (dragEvent && isEntityListOpen) {
        // Check if the drag came from our entity list (not from floating-notes-)
        const draggedData = dragEvent.active.data.current;
        const isFromEntityList =
          draggedData?.entity &&
          !dragEvent.active.id.toString().startsWith("floating-notes-");

        if (isFromEntityList && dragEvent.over?.id === "graph-canvas") {
          // Entity was successfully dropped on the graph
          // Keep container open for 0.5 seconds after successful drop
          if (dragCompleteTimeoutRef.current) {
            window.clearTimeout(dragCompleteTimeoutRef.current);
          }
          dragCompleteTimeoutRef.current = setTimeout(() => {
            // This will be handled by the onClose callback
          }, 500);
        }
      }
    });

    return unsubscribe;
  }, [isEntityListOpen]);

  // Initial load for entity cache
  useEffect(() => {
    loadOptions("").catch(console.error);
  }, [loadOptions]);

  const handleEntitySearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    setSearchTerm(value);
    loadOptions(value).catch(console.error);
  };

  const handleAddEntityClick = (): void => {
    // Expand the right sidebar if it's not already visible
    if (!userAppSettings.sidebarRightVisible) {
      $userAppSettings.setKey("sidebarRightVisible", true);
    }
    // Open the entity creation form
    $isEntityCreationFormOpen.set(true);
  };

  const handleEntityEnter = useCallback(
    (entity: Entity) => {
      if (!cytoscapeInstance || !graphId) return;

      selectEntity(entity);
      const position = getViewportCenter(cytoscapeInstance);

      const newNode = createGraphNodeModel({
        entity,
        position,
        value:
          entity.defaultValue === "" || entity.defaultValue == null
            ? "<value>"
            : entity.defaultValue,
      });

      if (newNode) {
        nodeOperations.addToGraph(graphId, newNode);

        // Keep container open for 0.5 seconds after entity is added
        if (dragCompleteTimeoutRef.current) {
          window.clearTimeout(dragCompleteTimeoutRef.current);
        }
        dragCompleteTimeoutRef.current = setTimeout(() => {
          // This will be handled by the onClose callback
        }, 500);
      }
    },
    [cytoscapeInstance, graphId, selectEntity],
  );

  // Show search results if there's a search term, otherwise show popular entities
  const displayedEntities = searchTerm
    ? searchResults
    : Object.values(entities).sort((a, b) => {
        // Sort entities alphabetically by name
        return a.name.localeCompare(b.name);
      });

  // Filter regular entities (exclude sticky note from search results)
  const regularEntities = displayedEntities.filter(
    (entity) => !isStickyNoteEntity(entity),
  );

  const entityListContent = (
    <div className="ring-.75 max-h-[400px] min-w-[280px] overflow-hidden rounded-lg bg-zinc-900/90 p-3 shadow-lg ring-zinc-600">
      <div className="mb-3">
        <div className="flex min-w-0 items-center gap-2">
          <InputGroup className="min-w-0 flex-1">
            <MagnifyingGlassIcon className="h-4 w-4 text-zinc-400" />
            <Input
              aria-label="Search entities"
              className="min-w-0 border-zinc-600 bg-zinc-800/50 text-zinc-200 placeholder-zinc-400"
              disabled={isLoading}
              name="search"
              onChange={handleEntitySearch}
              placeholder="Search entities..."
              value={searchTerm}
            />
          </InputGroup>

          <Button
            aria-label="Add Entity"
            className="flex-shrink-0"
            onClick={handleAddEntityClick}
            outline
            size="xs"
          >
            <PlusIcon />
          </Button>
        </div>
      </div>

      <div className="max-h-[320px] overflow-y-auto">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, index) => (
            <div
              className="mb-2 flex w-full min-w-0 animate-pulse items-center rounded p-2"
              key={`skeleton-${index}`}
            >
              <div className="h-5 w-5 flex-shrink-0 rounded bg-zinc-700" />
              <div className="ml-3 h-4 w-24 flex-shrink-0 rounded bg-zinc-700" />
            </div>
          ))
        ) : regularEntities.length > 0 ? (
          regularEntities.map((entity) => (
            <DraggableMenuItem
              entity={entity}
              icon={createIconComponent(entity.icon)}
              iconColor={colorSchemas[entity.colorSchema]?.text}
              key={entity.id}
              onEnterPress={handleEntityEnter}
              text={entity.name}
            />
          ))
        ) : (
          <p className="py-4 text-center text-sm text-zinc-400">
            No entities found
          </p>
        )}
      </div>
    </div>
  );

  return (
    <div className="relative">
      <div
        className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-white"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={entityListRef}
      >
        <PlusIcon className="h-5 w-5 text-zinc-900" />
      </div>

      {isEntityListOpen && (
        <div
          className="absolute left-full top-0 ml-4"
          onMouseEnter={handleMenuMouseEnter}
          onMouseLeave={handleMenuMouseLeave}
        >
          {entityListContent}
        </div>
      )}
    </div>
  );
};
